import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { 
  Percent,
  Save,
  FileSpreadsheet,
  TestTube,
  QrCode,
  Wifi,
  WifiOff,
  RefreshCw,
  CheckCircle,
  Loader2,
  Bot,
  MessageSquare,
  BookOpen,
  Sparkles,
  Link,
  AlertCircle,
  Users,
  Building2,
  Phone,
  Key,
  Database,
  CloudDownload,
  UserPlus,
  Edit,
  Trash2,
  Shield,
  Lock,
  Paintbrush,
  Type,
  Columns,
  Palette,
  ChevronRight
} from "lucide-react";
import OwnerChat from "@/components/OwnerChat";

export default function Settings() {
  const [settings, setSettings] = useState({
    twilio_whatsapp_number: "",
    google_sheet_id: "",
    max_discount_percent: 2.0,
    dispatch_default_days: 3,
    welcome_message: "Hello 👋 Welcome to Purelife Bot B2B Division.",
    bot_name: "Purelife Bot",
    bot_personality: "Professional, friendly, and helpful sales executive",
    bot_instructions: "",
    sample_responses: "",
    google_service_account_json: "",
    google_sheet_order_qty_name: "Order Qty",
    google_sheet_order_rates_name: "Order Rates",
    google_sheet_header_row: 4,
    // Owner & Company Config
    owner_phone: "",
    owner_name: "Admin",
    company_name: "Purelife Cashews",
    company_address: "",
    company_phone: "",
    company_email: "",
    company_gst: "",
    bank_name: "",
    bank_account: "",
    bank_ifsc: "",
    payment_terms: "50% Advance, 50% before dispatch",
    proforma_validity_days: 7,
    // Gemini API Key
    gemini_api_key: "",
    // Stock Sheet Sync
    stock_sheet_id: "",
    stock_sheet_tab: "Stock",
    stock_sync_enabled: false,
    stock_sync_interval_minutes: 15,
    // Low Stock Alert Settings
    low_stock_threshold: 10,
    low_stock_alert_enabled: true,
    // Multi-language Settings
    bot_language_mode: "auto"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // WhatsApp connection state
  const [waStatus, setWaStatus] = useState({ status: 'disconnected', connected: false });
  const [qrCode, setQrCode] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  
  // Google Sheets state
  const [sheetsStatus, setSheetsStatus] = useState(null);
  const [testingSheets, setTestingSheets] = useState(false);
  
  // Stock Sync state
  const [stockSyncStatus, setStockSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Test chat state
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("Hi, I want to buy cashews");
  const [testResponse, setTestResponse] = useState("");
  const [testing, setTesting] = useState(false);

  // User Management state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: "",
    password: "",
    role: "user",
    permissions: []
  });

  const allPermissions = [
    { id: "dashboard", label: "Dashboard" },
    { id: "customers", label: "Customers" },
    { id: "products", label: "Products" },
    { id: "orders", label: "Orders" },
    { id: "warehouse", label: "Warehouse" },
    { id: "crm", label: "CRM" },
    { id: "receipts", label: "Receipts" },
    { id: "returns", label: "Returns" },
    { id: "conversations", label: "Conversations" },
    { id: "reports", label: "Reports" },
    { id: "activity_log", label: "Activity Log" },
    { id: "settings", label: "Settings" }
  ];

  // Formatting/Styling state
  const [formatSettings, setFormatSettings] = useState({
    selectedWindow: 'orders',
    orders: { columnSpacing: 'normal', fontSize: 'medium', headerBold: true, primaryColor: '#16a34a', headerBgColor: '#f0fdf4' },
    warehouse: { columnSpacing: 'normal', fontSize: 'medium', headerBold: true, primaryColor: '#2563eb', headerBgColor: '#eff6ff' },
    crm: { columnSpacing: 'normal', fontSize: 'medium', headerBold: true, primaryColor: '#16a34a', headerBgColor: '#f0fdf4' },
    customers: { columnSpacing: 'normal', fontSize: 'medium', headerBold: true, primaryColor: '#7c3aed', headerBgColor: '#f5f3ff' },
    products: { columnSpacing: 'normal', fontSize: 'medium', headerBold: true, primaryColor: '#ea580c', headerBgColor: '#fff7ed' }
  });

  // Master - Account Types & Accounts state
  const [accountTypes, setAccountTypes] = useState([]);
  const [accountsList, setAccountsList] = useState([]);
  const [newAccountType, setNewAccountType] = useState('');
  const [newAccount, setNewAccount] = useState({ name: '', account_type: '', opening_balance: 0, description: '' });

  const fetchAccountTypes = async () => {
    try {
      const res = await axios.get(`${API}/account-types`);
      setAccountTypes(res.data);
    } catch (e) { /* silent */ }
  };

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/accounts`);
      setAccountsList(res.data);
    } catch (e) { /* silent */ }
  };

  const addAccountType = async () => {
    if (!newAccountType.trim()) return;
    try {
      await axios.post(`${API}/account-types`, { name: newAccountType.trim() });
      toast.success("Account type added");
      setNewAccountType('');
      fetchAccountTypes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteAccountType = async (id) => {
    try {
      await axios.delete(`${API}/account-types/${id}`);
      toast.success("Deleted");
      fetchAccountTypes();
    } catch (e) { toast.error("Failed"); }
  };

  const addAccount = async () => {
    if (!newAccount.name || !newAccount.account_type) { toast.error("Name and type required"); return; }
    try {
      await axios.post(`${API}/accounts`, newAccount);
      toast.success("Account created");
      setNewAccount({ name: '', account_type: '', opening_balance: 0, description: '' });
      fetchAccounts();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteAccount = async (id) => {
    try {
      await axios.delete(`${API}/accounts/${id}`);
      toast.success("Deleted");
      fetchAccounts();
    } catch (e) { toast.error("Failed"); }
  };
  const [savingFormat, setSavingFormat] = useState(false);

  const windowOptions = [
    { value: 'orders', label: 'Orders' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'crm', label: 'CRM' },
    { value: 'customers', label: 'Customers' },
    { value: 'products', label: 'Products' }
  ];

  const spacingOptions = [
    { value: 'compact', label: 'Compact' },
    { value: 'normal', label: 'Normal' },
    { value: 'relaxed', label: 'Relaxed' }
  ];

  const fontSizeOptions = [
    { value: 'small', label: 'Small (10px)' },
    { value: 'medium', label: 'Medium (12px)' },
    { value: 'large', label: 'Large (14px)' }
  ];

  useEffect(() => {
    fetchSettings();
    checkWhatsAppStatus();
    fetchUsers();
    loadFormatSettings();
    fetchAccountTypes();
    fetchAccounts();
  }, []);

  // Load format settings from localStorage
  const loadFormatSettings = () => {
    try {
      const saved = localStorage.getItem('purelife_format_settings');
      if (saved) {
        setFormatSettings(prev => ({ ...prev, ...JSON.parse(saved) }));
      }
    } catch (e) {
    }
  };

  // Save format settings to localStorage
  const saveFormatSettings = () => {
    setSavingFormat(true);
    try {
      localStorage.setItem('purelife_format_settings', JSON.stringify(formatSettings));
      toast.success('Format settings saved! Refresh pages to apply.');
    } catch (e) {
      toast.error('Failed to save format settings');
    } finally {
      setSavingFormat(false);
    }
  };

  // Update format setting for current window
  const updateFormatSetting = (key, value) => {
    const currentWindow = formatSettings.selectedWindow;
    setFormatSettings(prev => ({
      ...prev,
      [currentWindow]: {
        ...prev[currentWindow],
        [key]: value
      }
    }));
  };

  // Get current window settings
  const getCurrentWindowSettings = () => {
    return formatSettings[formatSettings.selectedWindow] || {};
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.username || !userForm.password) {
      toast.error("Username and password are required");
      return;
    }
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, userForm);
        toast.success("User updated");
      } else {
        await axios.post(`${API}/users`, userForm);
        toast.success("User created");
      }
      fetchUsers();
      setUserDialogOpen(false);
      resetUserForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      password: "",
      role: user.role,
      permissions: user.permissions || []
    });
    setUserDialogOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const resetUserForm = () => {
    setEditingUser(null);
    setUserForm({
      username: "",
      password: "",
      role: "user",
      permissions: []
    });
  };

  const togglePermission = (permissionId) => {
    setUserForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  // Poll for QR code when waiting for scan
  useEffect(() => {
    let interval;
    if (waStatus.status === 'waiting_for_scan' || waStatus.status === 'disconnected') {
      interval = setInterval(() => {
        checkWhatsAppStatus();
        fetchQRCode();
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [waStatus.status]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const checkWhatsAppStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`);
      setWaStatus(response.data);
      if (response.data.connected) {
        setQrCode(null);
      }
    } catch (error) {
      setWaStatus({ status: 'error', connected: false, error: error.message });
    }
  };

  const fetchQRCode = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/qr`);
      if (response.data.qr) {
        setQrCode(response.data.qr);
      }
    } catch (error) {
    }
  };

  const handleReconnect = async () => {
    setCheckingStatus(true);
    try {
      await axios.post(`${API}/whatsapp/reconnect`);
      toast.success("Reconnecting WhatsApp...");
      setTimeout(() => {
        checkWhatsAppStatus();
        fetchQRCode();
      }, 2000);
    } catch (error) {
      toast.error("Failed to reconnect");
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/whatsapp/disconnect`);
      toast.success("WhatsApp disconnected - session cleared");
      setWaStatus({ status: 'disconnected', connected: false });
      setQrCode(null);
      // Wait a bit then check for new QR code
      setTimeout(() => {
        checkWhatsAppStatus();
        fetchQRCode();
      }, 2000);
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const testGoogleSheets = async () => {
    setTestingSheets(true);
    setSheetsStatus(null);
    try {
      const response = await axios.post(`${API}/google-sheets/test`);
      setSheetsStatus(response.data);
      if (response.data.success) {
        toast.success("Google Sheets connected successfully!");
      } else {
        toast.error(response.data.error || "Connection failed");
      }
    } catch (error) {
      setSheetsStatus({ success: false, error: error.message });
      toast.error("Failed to test connection");
    } finally {
      setTestingSheets(false);
    }
  };

  const fetchStockSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/products/stock-sync-status`);
      setStockSyncStatus(response.data);
    } catch (error) {
    }
  };

  const handleSyncStock = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/products/sync-stock`);
      if (response.data.success) {
        toast.success(`Stock synced! Updated ${response.data.updated} products`);
        setStockSyncStatus(prev => ({
          ...prev,
          last_stock_sync: response.data.sync_time
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync stock");
    } finally {
      setSyncing(false);
    }
  };

  // Fetch stock sync status on load
  useEffect(() => {
    fetchStockSyncStatus();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success("Settings saved successfully! Bot will use new instructions.");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestMessage = async () => {
    if (!testPhone) {
      toast.error("Please enter a test phone number");
      return;
    }
    setTesting(true);
    setTestResponse("");
    try {
      const response = await axios.post(`${API}/whatsapp/test?phone=${encodeURIComponent(testPhone)}&message=${encodeURIComponent(testMessage)}`);
      setTestResponse(response.data.response);
      toast.success("Test message processed");
    } catch (error) {
      toast.error("Failed to process test message");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure Purelife Bot and customize AI behavior
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-settings-btn">
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save All Settings"}
        </Button>
      </div>

      <Tabs defaultValue="owner-chat" className="space-y-6">
        <TabsList className="grid w-full grid-cols-10 lg:w-[1200px]">
          <TabsTrigger value="owner-chat" className="gap-1 text-[11px]">
            <Users className="w-4 h-4" />
            Owner
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-1 text-[11px]">
            <Shield className="w-4 h-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="master" className="gap-1 text-[11px]">
            <Database className="w-4 h-4" />
            Master
          </TabsTrigger>
          <TabsTrigger value="formatting" className="gap-1 text-[11px]">
            <Paintbrush className="w-4 h-4" />
            Format
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="gap-1 text-[11px]">
            <QrCode className="w-4 h-4" />
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="company" className="gap-1 text-[11px]">
            <Building2 className="w-4 h-4" />
            Company
          </TabsTrigger>
          <TabsTrigger value="google-sheets" className="gap-1 text-[11px]">
            <FileSpreadsheet className="w-4 h-4" />
            Sheets
          </TabsTrigger>
          <TabsTrigger value="stock-sync" className="gap-1 text-[11px]">
            <Database className="w-4 h-4" />
            Stock
          </TabsTrigger>
          <TabsTrigger value="bot-script" className="gap-1 text-[11px]">
            <Bot className="w-4 h-4" />
            Bot
          </TabsTrigger>
          <TabsTrigger value="responses" className="gap-1 text-[11px]">
            <MessageSquare className="w-4 h-4" />
            Responses
          </TabsTrigger>
        </TabsList>

        {/* Owner Chat Tab */}
        <TabsContent value="owner-chat">
          <OwnerChat />
        </TabsContent>

        {/* Users Management Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    User Management
                  </CardTitle>
                  <CardDescription>Create users and manage their permissions</CardDescription>
                </div>
                <Dialog open={userDialogOpen} onOpenChange={(open) => { setUserDialogOpen(open); if (!open) resetUserForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="gap-2">
                      <UserPlus className="w-4 h-4" />
                      Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>{editingUser ? "Edit User" : "Create New User"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Username</Label>
                        <Input
                          value={userForm.username}
                          onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                          placeholder="Enter username"
                          disabled={editingUser?.role === 'owner'}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Password {editingUser && "(leave blank to keep current)"}</Label>
                        <Input
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                          placeholder={editingUser ? "Enter new password" : "Enter password"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Role</Label>
                        <Select
                          value={userForm.role}
                          onValueChange={(v) => setUserForm({ ...userForm, role: v })}
                          disabled={editingUser?.role === 'owner'}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="staff">Staff</SelectItem>
                            <SelectItem value="customer">Customer</SelectItem>
                            <SelectItem value="user">User</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Permissions</Label>
                        <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg">
                          {allPermissions.map((perm) => (
                            <div key={perm.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={perm.id}
                                checked={userForm.permissions.includes(perm.id)}
                                onCheckedChange={() => togglePermission(perm.id)}
                                disabled={userForm.role === 'owner'}
                              />
                              <label htmlFor={perm.id} className="text-sm">{perm.label}</label>
                            </div>
                          ))}
                        </div>
                        {userForm.role === 'owner' && (
                          <p className="text-xs text-muted-foreground">Owner has all permissions</p>
                        )}
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button variant="outline" onClick={() => { setUserDialogOpen(false); resetUserForm(); }}>
                          Cancel
                        </Button>
                        <Button onClick={handleCreateUser}>
                          {editingUser ? "Update" : "Create"}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (() => {
                // Group users by role
                const roleGroups = {};
                const roleOrder = ['owner', 'admin', 'staff', 'customer', 'guest', 'user'];
                const roleColors = { owner: 'bg-purple-100 text-purple-800', admin: 'bg-red-100 text-red-800', staff: 'bg-blue-100 text-blue-800', customer: 'bg-amber-100 text-amber-800', guest: 'bg-emerald-100 text-emerald-800', user: 'bg-gray-100 text-gray-800' };
                
                users.forEach(u => {
                  const role = u.role || 'user';
                  if (!roleGroups[role]) roleGroups[role] = [];
                  roleGroups[role].push(u);
                });
                
                const sortedRoles = [...new Set([...roleOrder, ...Object.keys(roleGroups)])].filter(r => roleGroups[r]?.length > 0);
                
                return (
                  <div className="space-y-3">
                    {sortedRoles.map(role => (
                      <Collapsible key={role} defaultOpen={false}>
                        <CollapsibleTrigger className="flex items-center gap-2 w-full p-2.5 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors">
                          <ChevronRight className="w-4 h-4 transition-transform data-[state=open]:rotate-90" />
                          <Badge className={roleColors[role] || 'bg-gray-100 text-gray-800'}>{role.toUpperCase()}</Badge>
                          <span className="text-sm text-muted-foreground">({roleGroups[role].length})</span>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="border rounded-lg overflow-hidden mt-1.5 ml-6">
                            <table className="w-full">
                              <thead className="bg-secondary/30">
                                <tr>
                                  <th className="text-left p-2.5 text-xs font-semibold">Username</th>
                                  <th className="text-left p-2.5 text-xs font-semibold">Password</th>
                                  {role === 'customer' && <th className="text-left p-2.5 text-xs font-semibold">Linked Customer</th>}
                                  <th className="text-left p-2.5 text-xs font-semibold">Permissions</th>
                                  <th className="text-left p-2.5 text-xs font-semibold">Status</th>
                                  <th className="text-right p-2.5 text-xs font-semibold">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {roleGroups[role].map(user => (
                                  <tr key={user.id} className="border-t">
                                    <td className="p-2.5">
                                      <div className="flex items-center gap-2">
                                        <Users className="w-3.5 h-3.5 text-muted-foreground" />
                                        <span className="font-medium text-sm">{user.username}</span>
                                      </div>
                                    </td>
                                    <td className="p-2.5">
                                      <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">{user.password_display || '••••••'}</code>
                                    </td>
                                    {role === 'customer' && (
                                      <td className="p-2.5 text-xs text-muted-foreground">{user.linked_customer || '-'}</td>
                                    )}
                                    <td className="p-2.5">
                                      {user.role === 'owner' ? (
                                        <span className="text-xs text-muted-foreground">All permissions</span>
                                      ) : (
                                        <div className="flex flex-wrap gap-1">
                                          {user.permissions?.slice(0, 3).map(p => (
                                            <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>
                                          ))}
                                          {user.permissions?.length > 3 && (
                                            <Badge variant="outline" className="text-[10px]">+{user.permissions.length - 3}</Badge>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                    <td className="p-2.5">
                                      <Badge className={user.is_active !== false ? 'bg-green-100 text-green-800 text-[10px]' : 'bg-red-100 text-red-800 text-[10px]'}>
                                        {user.is_active !== false ? 'Active' : 'Disabled'}
                                      </Badge>
                                    </td>
                                    <td className="p-2.5 text-right">
                                      <div className="flex justify-end gap-1">
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditUser(user)}>
                                          <Edit className="w-3.5 h-3.5" />
                                        </Button>
                                        {user.role !== 'owner' && (
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDeleteUser(user.id)}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Master Tab - Account Types & Accounts */}
        <TabsContent value="master">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Account Types */}
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
                  <Database className="w-5 h-5 text-blue-600" />
                  Account Types
                </CardTitle>
                <CardDescription>Define account categories (Bank, Cash, Party, etc.)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input placeholder="New account type name" value={newAccountType} onChange={e => setNewAccountType(e.target.value)} className="h-8 text-sm" onKeyDown={e => e.key === 'Enter' && addAccountType()} />
                  <Button size="sm" onClick={addAccountType} className="h-8"><UserPlus className="w-3 h-3 mr-1" /> Add</Button>
                </div>
                <div className="space-y-2">
                  {accountTypes.map(at => (
                    <div key={at.id} className="flex items-center justify-between p-2 border rounded-lg">
                      <Badge variant="outline">{at.name}</Badge>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => deleteAccountType(at.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                  {accountTypes.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No account types yet. Add Bank, Cash, Party etc.</p>}
                </div>
              </CardContent>
            </Card>

            {/* Accounts */}
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
                  <Building2 className="w-5 h-5 text-green-600" />
                  Accounts
                </CardTitle>
                <CardDescription>Create bank, cash, or party accounts for receipts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Account name" value={newAccount.name} onChange={e => setNewAccount({...newAccount, name: e.target.value})} className="h-8 text-sm" />
                  <Select value={newAccount.account_type} onValueChange={v => setNewAccount({...newAccount, account_type: v})}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {accountTypes.map(at => <SelectItem key={at.id} value={at.name}>{at.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" placeholder="Opening balance" value={newAccount.opening_balance || ''} onChange={e => setNewAccount({...newAccount, opening_balance: parseFloat(e.target.value) || 0})} className="h-8 text-sm" />
                  <Button size="sm" onClick={addAccount} className="h-8"><UserPlus className="w-3 h-3 mr-1" /> Create Account</Button>
                </div>
                <div className="space-y-2">
                  {accountsList.map(acc => (
                    <div key={acc.id} className="flex items-center justify-between p-2 border rounded-lg">
                      <div>
                        <span className="font-medium text-sm">{acc.name}</span>
                        <Badge variant="outline" className="ml-2 text-[10px]">{acc.account_type}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Bal: {acc.opening_balance || 0}</span>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => deleteAccount(acc.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                  {accountsList.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No accounts yet</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Formatting Tab */}
        <TabsContent value="formatting">
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Paintbrush className="w-5 h-5 text-purple-600" />
                UI Formatting Settings
              </CardTitle>
              <CardDescription>Customize the appearance of different sections</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Window Selector */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-semibold">
                  <Columns className="w-4 h-4" />
                  Select Window to Format
                </Label>
                <Select 
                  value={formatSettings.selectedWindow} 
                  onValueChange={(v) => setFormatSettings(prev => ({ ...prev, selectedWindow: v }))}
                >
                  <SelectTrigger className="w-[250px]">
                    <SelectValue placeholder="Select window" />
                  </SelectTrigger>
                  <SelectContent>
                    {windowOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Type className="w-4 h-4" />
                  Formatting for: <Badge variant="outline" className="ml-1">{formatSettings.selectedWindow.toUpperCase()}</Badge>
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Column Spacing */}
                  <div className="space-y-2">
                    <Label className="text-sm">Column Spacing</Label>
                    <Select 
                      value={getCurrentWindowSettings().columnSpacing || 'normal'} 
                      onValueChange={(v) => updateFormatSetting('columnSpacing', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {spacingOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">Adjust table column spacing</p>
                  </div>

                  {/* Font Size */}
                  <div className="space-y-2">
                    <Label className="text-sm">Font Size</Label>
                    <Select 
                      value={getCurrentWindowSettings().fontSize || 'medium'} 
                      onValueChange={(v) => updateFormatSetting('fontSize', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {fontSizeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">Text size in tables</p>
                  </div>

                  {/* Bold Headers */}
                  <div className="space-y-2">
                    <Label className="text-sm">Header Style</Label>
                    <div className="flex items-center gap-3 h-10">
                      <Checkbox 
                        id="headerBold"
                        checked={getCurrentWindowSettings().headerBold !== false}
                        onCheckedChange={(v) => updateFormatSetting('headerBold', v)}
                      />
                      <label htmlFor="headerBold" className="text-sm cursor-pointer">Bold Headers</label>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Make table headers bold</p>
                  </div>

                  {/* Primary Color */}
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-2">
                      <Palette className="w-3 h-3" />
                      Primary Color
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        type="color"
                        value={getCurrentWindowSettings().primaryColor || '#16a34a'}
                        onChange={(e) => updateFormatSetting('primaryColor', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input 
                        value={getCurrentWindowSettings().primaryColor || '#16a34a'}
                        onChange={(e) => updateFormatSetting('primaryColor', e.target.value)}
                        className="w-28 h-10 text-xs font-mono"
                        placeholder="#16a34a"
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground">Accent/highlight color</p>
                  </div>

                  {/* Header Background Color */}
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-2">
                      <Palette className="w-3 h-3" />
                      Header Background
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        type="color"
                        value={getCurrentWindowSettings().headerBgColor || '#f0fdf4'}
                        onChange={(e) => updateFormatSetting('headerBgColor', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input 
                        value={getCurrentWindowSettings().headerBgColor || '#f0fdf4'}
                        onChange={(e) => updateFormatSetting('headerBgColor', e.target.value)}
                        className="w-28 h-10 text-xs font-mono"
                        placeholder="#f0fdf4"
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground">Table header background</p>
                  </div>
                </div>

                {/* Preview Box */}
                <div className="mt-6 border rounded-lg p-4 bg-secondary/20">
                  <Label className="text-sm font-semibold mb-3 block">Preview</Label>
                  <div 
                    className="border rounded overflow-hidden"
                    style={{ 
                      fontSize: getCurrentWindowSettings().fontSize === 'small' ? '10px' : getCurrentWindowSettings().fontSize === 'large' ? '14px' : '12px'
                    }}
                  >
                    <div 
                      className="px-3 py-2 border-b"
                      style={{ 
                        backgroundColor: getCurrentWindowSettings().headerBgColor || '#f0fdf4',
                        fontWeight: getCurrentWindowSettings().headerBold !== false ? 'bold' : 'normal',
                        color: getCurrentWindowSettings().primaryColor || '#16a34a'
                      }}
                    >
                      Sample Header - {formatSettings.selectedWindow.toUpperCase()}
                    </div>
                    <table className="w-full">
                      <thead>
                        <tr style={{ backgroundColor: getCurrentWindowSettings().headerBgColor || '#f0fdf4' }}>
                          <th 
                            className={`text-left border-b ${getCurrentWindowSettings().columnSpacing === 'compact' ? 'px-1 py-0.5' : getCurrentWindowSettings().columnSpacing === 'relaxed' ? 'px-4 py-3' : 'px-2 py-1.5'}`}
                            style={{ fontWeight: getCurrentWindowSettings().headerBold !== false ? 'bold' : 'normal' }}
                          >Order</th>
                          <th 
                            className={`text-left border-b ${getCurrentWindowSettings().columnSpacing === 'compact' ? 'px-1 py-0.5' : getCurrentWindowSettings().columnSpacing === 'relaxed' ? 'px-4 py-3' : 'px-2 py-1.5'}`}
                            style={{ fontWeight: getCurrentWindowSettings().headerBold !== false ? 'bold' : 'normal' }}
                          >Customer</th>
                          <th 
                            className={`text-right border-b ${getCurrentWindowSettings().columnSpacing === 'compact' ? 'px-1 py-0.5' : getCurrentWindowSettings().columnSpacing === 'relaxed' ? 'px-4 py-3' : 'px-2 py-1.5'}`}
                            style={{ fontWeight: getCurrentWindowSettings().headerBold !== false ? 'bold' : 'normal' }}
                          >Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className={`border-b ${getCurrentWindowSettings().columnSpacing === 'compact' ? 'px-1 py-0.5' : getCurrentWindowSettings().columnSpacing === 'relaxed' ? 'px-4 py-3' : 'px-2 py-1.5'}`}>
                            <span style={{ color: getCurrentWindowSettings().primaryColor || '#16a34a' }} className="font-mono font-semibold">E03001</span>
                          </td>
                          <td className={`border-b ${getCurrentWindowSettings().columnSpacing === 'compact' ? 'px-1 py-0.5' : getCurrentWindowSettings().columnSpacing === 'relaxed' ? 'px-4 py-3' : 'px-2 py-1.5'}`}>Sample Customer</td>
                          <td className={`text-right border-b ${getCurrentWindowSettings().columnSpacing === 'compact' ? 'px-1 py-0.5' : getCurrentWindowSettings().columnSpacing === 'relaxed' ? 'px-4 py-3' : 'px-2 py-1.5'}`}>50 kg</td>
                        </tr>
                        <tr>
                          <td className={`${getCurrentWindowSettings().columnSpacing === 'compact' ? 'px-1 py-0.5' : getCurrentWindowSettings().columnSpacing === 'relaxed' ? 'px-4 py-3' : 'px-2 py-1.5'}`}>
                            <span style={{ color: getCurrentWindowSettings().primaryColor || '#16a34a' }} className="font-mono font-semibold">E03002</span>
                          </td>
                          <td className={`${getCurrentWindowSettings().columnSpacing === 'compact' ? 'px-1 py-0.5' : getCurrentWindowSettings().columnSpacing === 'relaxed' ? 'px-4 py-3' : 'px-2 py-1.5'}`}>Another Customer</td>
                          <td className={`text-right ${getCurrentWindowSettings().columnSpacing === 'compact' ? 'px-1 py-0.5' : getCurrentWindowSettings().columnSpacing === 'relaxed' ? 'px-4 py-3' : 'px-2 py-1.5'}`}>100 kg</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveFormatSettings} disabled={savingFormat} className="gap-2">
                  {savingFormat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Format Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Tab */}
        <TabsContent value="whatsapp">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* WhatsApp Connection - QR Code */}
            <Card className="border-border/60 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <QrCode className="w-5 h-5 text-primary" />
                  WhatsApp Connection
                </CardTitle>
                <CardDescription>
                  Scan the QR code with your WhatsApp to connect
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  {/* QR Code Display */}
                  <div className="flex flex-col items-center">
                    <div className="w-64 h-64 bg-white rounded-lg border-2 border-dashed border-border flex items-center justify-center overflow-hidden">
                      {waStatus.connected ? (
                        <div className="text-center p-4">
                          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-3" />
                          <p className="font-semibold text-green-700">Connected!</p>
                          {waStatus.user && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {waStatus.user.name || waStatus.user.id}
                            </p>
                          )}
                        </div>
                      ) : qrCode ? (
                        <img src={qrCode} alt="WhatsApp QR Code" className="w-full h-full object-contain p-2" />
                      ) : (
                        <div className="text-center p-4">
                          <Loader2 className="w-12 h-12 text-muted-foreground mx-auto mb-3 animate-spin" />
                          <p className="text-sm text-muted-foreground">Generating QR Code...</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Status Badge */}
                    <div className="mt-4">
                      <Badge 
                        className={`${
                          waStatus.connected 
                            ? 'bg-green-100 text-green-800 border-green-200' 
                            : waStatus.status === 'waiting_for_scan'
                            ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                            : 'bg-red-100 text-red-800 border-red-200'
                        }`}
                      >
                        {waStatus.connected ? (
                          <><Wifi className="w-3 h-3 mr-1" /> Connected</>
                        ) : waStatus.status === 'waiting_for_scan' ? (
                          <><QrCode className="w-3 h-3 mr-1" /> Waiting for Scan</>
                        ) : (
                          <><WifiOff className="w-3 h-3 mr-1" /> Disconnected</>
                        )}
                      </Badge>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="flex-1">
                    <h3 className="font-semibold mb-4" style={{ fontFamily: 'Manrope' }}>
                      How to Connect WhatsApp
                    </h3>
                    <ol className="space-y-3 text-sm text-muted-foreground">
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-semibold">1</span>
                        <span>Open WhatsApp on your phone</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-semibold">2</span>
                        <span>Tap <strong>Menu ⋮</strong> or <strong>Settings</strong> → <strong>Linked Devices</strong></span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-semibold">3</span>
                        <span>Tap <strong>Link a Device</strong></span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-semibold">4</span>
                        <span>Point your phone camera at the QR code above</span>
                      </li>
                    </ol>

                    <div className="flex gap-3 mt-6">
                      {waStatus.connected ? (
                        <Button variant="destructive" onClick={handleDisconnect} data-testid="disconnect-wa-btn">
                          <WifiOff className="w-4 h-4 mr-2" />
                          Disconnect
                        </Button>
                      ) : (
                        <Button onClick={handleReconnect} disabled={checkingStatus} data-testid="reconnect-wa-btn">
                          {checkingStatus ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                          )}
                          Refresh QR Code
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Test Chat */}
            <Card className="border-border/60 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <TestTube className="w-5 h-5 text-primary" />
                  Test Bot Response
                </CardTitle>
                <CardDescription>
                  Test how Purelife Bot responds with your current settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="test_phone">Test Phone Number</Label>
                      <Input
                        id="test_phone"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        placeholder="+919876543210"
                        data-testid="test-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="test_message">Test Message</Label>
                      <Textarea
                        id="test_message"
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        rows={3}
                        data-testid="test-message-input"
                      />
                    </div>
                    <Button 
                      onClick={handleTestMessage} 
                      disabled={testing}
                      className="w-full"
                      data-testid="test-chat-btn"
                    >
                      {testing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> Test Bot Response</>
                      )}
                    </Button>
                  </div>
                  <div>
                    <Label className="mb-2 block">Bot Response:</Label>
                    <div className={`p-4 rounded-lg min-h-[150px] ${testResponse ? 'bg-green-50 border border-green-200' : 'bg-secondary/50'}`}>
                      {testResponse ? (
                        <p className="text-sm text-green-900 whitespace-pre-wrap">{testResponse}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground">Response will appear here after testing...</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Google Sheets Tab */}
        <TabsContent value="google-sheets">
          <div className="space-y-6">
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <FileSpreadsheet className="w-5 h-5 text-primary" />
                  Google Sheets Integration
                </CardTitle>
                <CardDescription>
                  Connect Google Sheets to automatically post confirmed orders
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="sheet_id">Google Sheet ID *</Label>
                      <Input
                        id="sheet_id"
                        value={settings.google_sheet_id || ""}
                        onChange={(e) => setSettings({ ...settings, google_sheet_id: e.target.value })}
                        placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                        data-testid="sheet-id-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Found in the Google Sheets URL after /d/
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="qty_sheet_name">Order Qty Sheet Name</Label>
                      <Input
                        id="qty_sheet_name"
                        value={settings.google_sheet_order_qty_name || "Order Qty"}
                        onChange={(e) => setSettings({ ...settings, google_sheet_order_qty_name: e.target.value })}
                        placeholder="Order Qty"
                        data-testid="qty-sheet-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="rates_sheet_name">Order Rates Sheet Name</Label>
                      <Input
                        id="rates_sheet_name"
                        value={settings.google_sheet_order_rates_name || "Order Rates"}
                        onChange={(e) => setSettings({ ...settings, google_sheet_order_rates_name: e.target.value })}
                        placeholder="Order Rates"
                        data-testid="rates-sheet-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="header_row">Header Row Number</Label>
                      <Input
                        id="header_row"
                        type="number"
                        min="1"
                        value={settings.google_sheet_header_row || 4}
                        onChange={(e) => setSettings({ ...settings, google_sheet_header_row: parseInt(e.target.value) || 4 })}
                        placeholder="4"
                        data-testid="header-row-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Row number containing grade headers (W180, W240, etc.)
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="service_account">Service Account JSON *</Label>
                      <Textarea
                        id="service_account"
                        value={settings.google_service_account_json || ""}
                        onChange={(e) => setSettings({ ...settings, google_service_account_json: e.target.value })}
                        placeholder='{"type": "service_account", "project_id": "...", ...}'
                        rows={8}
                        className="font-mono text-xs"
                        data-testid="service-account-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Paste the entire JSON content from your service account key file
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <Button onClick={testGoogleSheets} disabled={testingSheets} variant="outline" className="gap-2">
                    {testingSheets ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Testing...</>
                    ) : (
                      <><Link className="w-4 h-4" /> Test Connection</>
                    )}
                  </Button>
                  
                  {sheetsStatus && (
                    <div className={`flex items-center gap-2 ${sheetsStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                      {sheetsStatus.success ? (
                        <><CheckCircle className="w-4 h-4" /> Connected to "{sheetsStatus.spreadsheet_title}"</>
                      ) : (
                        <><AlertCircle className="w-4 h-4" /> {sheetsStatus.error}</>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="p-4 bg-secondary/50 rounded-lg space-y-3">
                  <h4 className="font-medium">Setup Instructions:</h4>
                  <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                    <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google Cloud Console</a></li>
                    <li>Create a project and enable Google Sheets API</li>
                    <li>Create a Service Account and download the JSON key</li>
                    <li>Share your Google Sheet with the service account email (Editor access)</li>
                    <li>Create two sheets in your spreadsheet: "Order Qty" and "Order Rates"</li>
                    <li>Paste the JSON key content above and test the connection</li>
                  </ol>
                </div>
                
                <div className="p-4 bg-primary/5 rounded-lg">
                  <h4 className="font-medium mb-2">What gets posted:</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-primary">Order Qty Sheet:</p>
                      <p className="text-muted-foreground">Order No, Date, Customer, Company, Grade, Quantity</p>
                    </div>
                    <div>
                      <p className="font-medium text-primary">Order Rates Sheet:</p>
                      <p className="text-muted-foreground">Order No, Date, Customer, Grade, Rate, Quantity, Total</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Stock Sync Tab */}
        <TabsContent value="stock-sync">
          <div className="space-y-6">
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Database className="w-5 h-5 text-primary" />
                  Live Stock Sync from Google Sheet
                </CardTitle>
                <CardDescription>
                  Sync product stock quantities from your Google Sheet in real-time
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Sync Status */}
                <div className={`p-4 rounded-lg border ${stockSyncStatus?.last_stock_sync ? 'bg-green-50 border-green-200' : 'bg-secondary/50 border-border'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        {stockSyncStatus?.last_stock_sync ? (
                          <><CheckCircle className="w-4 h-4 text-green-600" /> Stock Synced</>
                        ) : (
                          <><AlertCircle className="w-4 h-4 text-amber-600" /> Not Yet Synced</>
                        )}
                      </p>
                      {stockSyncStatus?.last_stock_sync && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Last sync: {new Date(stockSyncStatus.last_stock_sync).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <Button 
                      onClick={handleSyncStock} 
                      disabled={syncing || !settings.stock_sheet_id || !settings.google_service_account_json}
                      className="gap-2"
                    >
                      {syncing ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Syncing...</>
                      ) : (
                        <><CloudDownload className="w-4 h-4" /> Sync Now</>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Configuration */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="stock_sheet_id">Stock Sheet ID *</Label>
                    <Input
                      id="stock_sheet_id"
                      value={settings.stock_sheet_id || ""}
                      onChange={(e) => setSettings({ ...settings, stock_sheet_id: e.target.value })}
                      placeholder="1vdNOxknPCwYFJrmX_6-SzcxGH8XYXpfEPYjpj..."
                      data-testid="stock-sheet-id-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Copy from the URL: docs.google.com/spreadsheets/d/<strong>SHEET_ID</strong>/edit
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stock_sheet_tab">Tab Name</Label>
                    <Input
                      id="stock_sheet_tab"
                      value={settings.stock_sheet_tab || "Stock"}
                      onChange={(e) => setSettings({ ...settings, stock_sheet_tab: e.target.value })}
                      placeholder="Stock"
                      data-testid="stock-sheet-tab-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      The tab/sheet name containing stock data (e.g., "Stock")
                    </p>
                  </div>
                </div>

                {/* Requirements */}
                <div className="p-4 bg-secondary/50 rounded-lg">
                  <h4 className="font-medium mb-2">Requirements:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      {settings.google_service_account_json ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                      )}
                      Google Service Account configured (in Sheets tab)
                    </li>
                    <li className="flex items-center gap-2">
                      {settings.stock_sheet_id ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                      )}
                      Stock Sheet ID configured
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Sheet shared with service account email
                    </li>
                  </ul>
                </div>

                {/* Sheet Format Info */}
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Expected Sheet Format:</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Your sheet should have the following structure:
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li><strong>Column A:</strong> Grade (W180, W240, W320, etc.)</li>
                    <li><strong>Column H:</strong> IN STOCK (TIN packing)</li>
                    <li><strong>Column N:</strong> Grade (BUCKET packing)</li>
                    <li><strong>Column T:</strong> IN STOCK (BUCKET packing)</li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-2">
                    TIN + BUCKET stock will be combined for each grade.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Low Stock Alert Settings */}
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  Low Stock Alerts
                </CardTitle>
                <CardDescription>
                  Get notified when stock falls below threshold
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="low_stock_alert_enabled"
                    checked={settings.low_stock_alert_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, low_stock_alert_enabled: checked })}
                  />
                  <Label htmlFor="low_stock_alert_enabled">
                    Enable Low Stock Alerts via WhatsApp
                  </Label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="low_stock_threshold">Stock Threshold</Label>
                  <Input
                    id="low_stock_threshold"
                    type="number"
                    value={settings.low_stock_threshold || 10}
                    onChange={(e) => setSettings({ ...settings, low_stock_threshold: parseInt(e.target.value) || 10 })}
                    placeholder="10"
                    className="w-32"
                  />
                  <p className="text-xs text-muted-foreground">
                    Alert when any product stock falls to this level or below
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Company/Proforma Tab */}
        <TabsContent value="company">
          <div className="space-y-6">
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Building2 className="w-5 h-5 text-primary" />
                  Owner & Company Details
                </CardTitle>
                <CardDescription>
                  Configure owner contact and company information for proforma invoices
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Owner Contact */}
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Owner WhatsApp (Required for Order Flow)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Owner Name</Label>
                      <Input
                        value={settings.owner_name || ""}
                        onChange={(e) => setSettings({ ...settings, owner_name: e.target.value })}
                        placeholder="Admin"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Owner WhatsApp Number *</Label>
                      <Input
                        value={settings.owner_phone || ""}
                        onChange={(e) => setSettings({ ...settings, owner_phone: e.target.value })}
                        placeholder="+919876543210"
                      />
                      <p className="text-xs text-muted-foreground">
                        Proforma invoices and queries will be sent to this number
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Company Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={settings.company_name || ""}
                      onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                      placeholder="Purelife Cashews"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company Phone</Label>
                    <Input
                      value={settings.company_phone || ""}
                      onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Company Address</Label>
                  <Textarea
                    value={settings.company_address || ""}
                    onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                    placeholder="123 Business Street, Mumbai, Maharashtra 400001"
                    rows={2}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Email</Label>
                    <Input
                      value={settings.company_email || ""}
                      onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                      placeholder="sales@purelife.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>GST Number</Label>
                    <Input
                      value={settings.company_gst || ""}
                      onChange={(e) => setSettings({ ...settings, company_gst: e.target.value })}
                      placeholder="27XXXXX1234X1ZX"
                    />
                  </div>
                </div>
                
                {/* Bank Details */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-4">Bank Details (for Proforma)</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Bank Name</Label>
                      <Input
                        value={settings.bank_name || ""}
                        onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                        placeholder="HDFC Bank"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Account Number</Label>
                      <Input
                        value={settings.bank_account || ""}
                        onChange={(e) => setSettings({ ...settings, bank_account: e.target.value })}
                        placeholder="1234567890123"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>IFSC Code</Label>
                      <Input
                        value={settings.bank_ifsc || ""}
                        onChange={(e) => setSettings({ ...settings, bank_ifsc: e.target.value })}
                        placeholder="HDFC0001234"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Payment Terms */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Payment Terms</Label>
                    <Input
                      value={settings.payment_terms || ""}
                      onChange={(e) => setSettings({ ...settings, payment_terms: e.target.value })}
                      placeholder="50% Advance, 50% before dispatch"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Proforma Validity (Days)</Label>
                    <Input
                      type="number"
                      value={settings.proforma_validity_days || 7}
                      onChange={(e) => setSettings({ ...settings, proforma_validity_days: parseInt(e.target.value) })}
                      min={1}
                      max={30}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Bot Script Tab */}
        <TabsContent value="bot-script">
          <div className="space-y-6">
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Bot className="w-5 h-5 text-primary" />
                  Bot Identity & Personality
                </CardTitle>
                <CardDescription>
                  Define who your bot is and how it should behave
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bot_name">Bot Name</Label>
                    <Input
                      id="bot_name"
                      value={settings.bot_name || ""}
                      onChange={(e) => setSettings({ ...settings, bot_name: e.target.value })}
                      placeholder="Purelife Bot"
                      data-testid="bot-name-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      This name will be used when the bot introduces itself
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bot_personality">Bot Personality</Label>
                    <Input
                      id="bot_personality"
                      value={settings.bot_personality || ""}
                      onChange={(e) => setSettings({ ...settings, bot_personality: e.target.value })}
                      placeholder="Professional, friendly, and helpful sales executive"
                      data-testid="bot-personality-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Describe the tone and character of your bot
                    </p>
                  </div>
                </div>
                
                {/* Language Mode */}
                <div className="space-y-2">
                  <Label htmlFor="bot_language_mode">Response Language</Label>
                  <Select
                    value={settings.bot_language_mode || "auto"}
                    onValueChange={(value) => setSettings({ ...settings, bot_language_mode: value })}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-detect</SelectItem>
                      <SelectItem value="english">English Only</SelectItem>
                      <SelectItem value="hindi">Hindi Only</SelectItem>
                      <SelectItem value="hinglish">Hinglish Only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Auto-detect will respond in the same language the customer uses
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="welcome_message">Welcome Message</Label>
                  <Textarea
                    id="welcome_message"
                    value={settings.welcome_message || ""}
                    onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
                    rows={2}
                    placeholder="Hello 👋 Welcome to Purelife Bot B2B Division."
                    data-testid="welcome-message-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    First message sent to new customers
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Key className="w-5 h-5 text-primary" />
                  Gemini AI Configuration
                </CardTitle>
                <CardDescription>
                  Configure your own Gemini API key for AI-powered conversations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="gemini_api_key">Gemini API Key</Label>
                  <Input
                    id="gemini_api_key"
                    type="password"
                    value={settings.gemini_api_key || ""}
                    onChange={(e) => setSettings({ ...settings, gemini_api_key: e.target.value })}
                    placeholder="AIzaSy..."
                    data-testid="gemini-api-key-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Your own Google Gemini API key. Get one from <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google AI Studio</a>
                  </p>
                </div>
                {settings.gemini_api_key && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">Custom Gemini API key configured</span>
                  </div>
                )}
                {!settings.gemini_api_key && (
                  <div className="p-3 bg-secondary/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      No custom key configured. The bot will use the default Emergent LLM key.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <BookOpen className="w-5 h-5 text-primary" />
                  Bot Instructions Script
                </CardTitle>
                <CardDescription>
                  Write detailed instructions for how the bot should behave, negotiate, and respond
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="bot_instructions">Instructions (Bot will follow these rules)</Label>
                  <Textarea
                    id="bot_instructions"
                    value={settings.bot_instructions || ""}
                    onChange={(e) => setSettings({ ...settings, bot_instructions: e.target.value })}
                    rows={16}
                    className="font-mono text-sm"
                    placeholder={`Example instructions:

GREETING RULES:
- For new customers: Greet warmly and start onboarding
- For returning customers: Greet by name

CONVERSATION STYLE:
- Keep messages short and clear
- Be professional and polite
- Use customer's language preference

BARGAINING RULES:
- Never reveal base price or margins
- Start with base price
- For bulk orders (1000+ kg): Offer max discount
- For regular orders: Offer half discount
- Always justify discounts with quantity

CLOSING RULES:
- Summarize order before confirmation
- Mention delivery timeline
- Ask for confirmation`}
                    data-testid="bot-instructions-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Write rules for greetings, conversation style, bargaining tactics, and closing deals. The bot will follow these instructions strictly.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sample Responses Tab */}
        <TabsContent value="responses">
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <MessageSquare className="w-5 h-5 text-primary" />
                Sample Responses Library
              </CardTitle>
              <CardDescription>
                Provide example responses for the bot to learn from and reference
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="sample_responses">Sample Responses (Bot will use these as reference)</Label>
                <Textarea
                  id="sample_responses"
                  value={settings.sample_responses || ""}
                  onChange={(e) => setSettings({ ...settings, sample_responses: e.target.value })}
                  rows={20}
                  className="font-mono text-sm"
                  placeholder={`WELCOME EXAMPLES:
- "Hello! 👋 Welcome to Purelife Bot. I'm here to help you with premium cashew kernels. May I know your name?"
- "Namaste! 🙏 Purelife Bot mein aapka swagat hai. Kaise madad kar sakta hoon?"

PRICE INQUIRY EXAMPLES:
- "W240 grade is available at ₹1,250/kg for orders above 500kg. For bulk orders of 1000+ kg, I can offer a special rate."
- "Great choice! W320 is our most popular grade at ₹1,150/kg. What quantity are you looking for?"

NEGOTIATION EXAMPLES:
- "I understand you're looking for better pricing. For an order of 1000kg, I can offer ₹1,225/kg - that's our best rate."
- "Let me check what I can do... For your loyalty, I can offer 1.5% discount on this order."

OBJECTION HANDLING:
- "I appreciate your concern about pricing. Our cashews are premium quality with consistent grading. The value you get is worth every rupee."
- "I understand budget is important. How about we start with a smaller trial order of 500kg to experience our quality?"

CLOSING EXAMPLES:
- "Perfect! Let me confirm: W240, 500kg at ₹1,250/kg = ₹6,25,000. Delivery to Mumbai in 3-5 days. Shall I confirm?"
- "Order confirmed! ✅ Your order #ORD-123 for 1000kg W320 is being processed."`}
                  data-testid="sample-responses-input"
                />
                <p className="text-xs text-muted-foreground">
                  Add sample responses for different scenarios: welcomes, price inquiries, negotiations, objection handling, and closings. The bot will adapt these to fit conversations.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Tab */}
        <TabsContent value="pricing">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Percent className="w-5 h-5 text-primary" />
                  Pricing & Negotiation Rules
                </CardTitle>
                <CardDescription>
                  Configure pricing limits for bot negotiations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="max_discount">Maximum Discount (%)</Label>
                  <Input
                    id="max_discount"
                    type="number"
                    step="0.1"
                    min="0"
                    max="20"
                    value={settings.max_discount_percent}
                    onChange={(e) => setSettings({ ...settings, max_discount_percent: parseFloat(e.target.value) })}
                    data-testid="max-discount-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Bot can negotiate up to ±{settings.max_discount_percent}% of base price
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dispatch_days">Default Dispatch Days</Label>
                  <Input
                    id="dispatch_days"
                    type="number"
                    min="1"
                    max="30"
                    value={settings.dispatch_default_days}
                    onChange={(e) => setSettings({ ...settings, dispatch_default_days: parseInt(e.target.value) })}
                    data-testid="dispatch-days-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Default delivery timeline mentioned to customers
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <FileSpreadsheet className="w-5 h-5 text-primary" />
                  Google Sheets Integration
                </CardTitle>
                <CardDescription>
                  Sync data with Google Sheets (Optional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sheet_id">Google Sheet ID</Label>
                  <Input
                    id="sheet_id"
                    value={settings.google_sheet_id || ""}
                    onChange={(e) => setSettings({ ...settings, google_sheet_id: e.target.value })}
                    placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                    data-testid="sheet-id-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Found in the Google Sheets URL after /d/
                  </p>
                </div>
                <div className="p-4 bg-secondary/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Note: Google Sheets sync requires OAuth setup. Contact admin to configure.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
